import React, { useState, useEffect, useRef, useMemo } from 'react';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import ModalPortal from 'app/components/common/ModalPortal';
import AlignBox from 'app/components/common/AlignBox';
import Button from 'app/components/common/Button';
import HeadInfo from 'app/components/common/HeadInfo';
import useEnlog from 'app/hook/common/useEnlog';

import classNames from 'classnames/bind';
import styles from './LayerComm.module.scss';

const cx = classNames.bind(styles);

function LayerComm(props) {
	const {
		title = '',
		children = null,
		useSubmitBtn = false,
		useCancelBtn = false,
		useCloseBtn = false,
		useDaysClose = false,
		submitBtnText = '확인',
		cancelBtnText = '취소',
		daysCloseBtnText = '오늘 더 이상 보지 않기',
		onSubmit = undefined,
		onCancel = undefined,
		onClose = undefined,
		onDaysClose = undefined,
		layerClass = '',
		alignCenter = false,
		effect = 'fade',
		testId = 'layer',
		dimmedClick = true,
		disableSubmit = false,
		layerSize = '',
		btnSize = '',
		btnType = '',
		maxWidth = 'none',
		bottomBtn = undefined,
		innerScrollArea = '',
		animationNone = false,
	} = props;

	const [effectOn, setEffectOn] = useState(false);
	const layerRef = useRef();

	// eventNameGroup 설정 용도
	const { propsDataset } = useEnlog();
	const dataSet = propsDataset(props);

	useEffect(() => {
		setTimeout(() => setEffectOn(true), 0);
		const scrollOption = innerScrollArea ? { allowTouchMove: (el) => !!el?.closest(innerScrollArea) } : {};
		disableBodyScroll(layerRef.current, scrollOption);
		return () => clearAllBodyScrollLocks();
	}, [innerScrollArea]);

	const handleClose = () => {
		if (effect === 'fade') {
			setEffectOn(false);
			setTimeout(() => onClose && onClose(), 200);
		} else {
			if (onClose) {
				onClose();
			}

			if (onDaysClose) {
				onDaysClose();
			}
		}
		enableBodyScroll(layerRef.current);
	};

	const uiLayerBoxStyle = useMemo(() => {
		let styleObj = {};
		if (layerSize) {
			styleObj = { ...styleObj, width: layerSize, maxWidth: 'none' };
		}

		if (maxWidth !== 'none') {
			styleObj = {
				...styleObj,
				maxWidth,
				left: '50%',
				transform: 'translate3d(-50%,0,0)',
			};
		}
		return styleObj;
	}, [layerSize, maxWidth]);

	return (
		<ModalPortal>
			<HeadInfo metaThemeColor="#4c4c4c" />
			<div
				className={cx('uilayer', layerClass, 'fixed', effect, { effect: effectOn, animation_none: animationNone })}
				data-testid={testId}
				ref={layerRef}
			>
				<div className={cx('uilayer_wrap')}>
					<div className={cx('uilayer_contents')}>
						<div
							className={cx('uilayer_box')}
							style={uiLayerBoxStyle}
						>
							{title && <strong className={cx('base_ttl', { txt_center: alignCenter })}>{title}</strong>}

							{children !== null && <div className={cx('base_desc', { txt_center: alignCenter })}>{children}</div>}
							{(useCancelBtn || useSubmitBtn) && (
								<div
									className={cx('base_btn')}
									style={{ width: btnSize }}
								>
									<AlignBox type="flex">
										{useCancelBtn && (
											<Button
												type={btnType}
												domType="button"
												color="white"
												size="40"
												onClick={onCancel}
												title={cancelBtnText}
												{...dataSet}
											/>
										)}
										{useSubmitBtn && (
											<Button
												type={btnType}
												domType="button"
												color="red"
												size="40"
												onClick={onSubmit}
												title={submitBtnText}
												disabled={disableSubmit}
												{...dataSet}
											/>
										)}
									</AlignBox>
								</div>
							)}
							{bottomBtn}
							{useCloseBtn && (
								<button
									type="button"
									className={cx('uilayer_x')}
									onClick={handleClose}
									{...dataSet}
								>
									<span className={cx('blind')}>닫기</span>
								</button>
							)}
							{layerClass === 'uilayer_notice' && (
								<div className={cx('uilayer_btn')}>
									{useDaysClose && (
										<button
											type="button"
											className={cx('daysClose')}
											onClick={handleClose}
											{...dataSet}
										>
											{daysCloseBtnText}
										</button>
									)}
									<button
										type="button"
										className={cx('close')}
										ui-btn-action="close"
										onClick={handleClose}
										{...dataSet}
									>
										<span>닫기</span>
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
				<div
					className={cx('uilayer_dimmed')}
					onClick={dimmedClick ? handleClose : (e) => e.preventDefault()}
					role="presentation"
					data-testid="modalLayerDimmed"
				/>
			</div>
		</ModalPortal>
	);
}

export default LayerComm;
